import { Component, computed, inject, input, InputSignal, OnInit, output, OutputEmitterRef, Signal, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MDAlertService, MDIconComponent } from '@melodic-ui/components';

@Component({
	selector: 'lb-profile-image-loader',
	standalone: true,
	imports: [CommonModule, MDIconComponent],
	templateUrl: './profile-image-loader.component.html',
	styleUrl: './profile-image-loader.component.scss'
})
export class ProfileImageLoaderComponent implements OnInit {
	private _alertService: MDAlertService = inject(MDAlertService);

	public userImage: InputSignal<string | undefined> = input<string>();
	public dataUrl: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

	public onFileChange: OutputEmitterRef<string | undefined> = output<string>();

	public profileImage: Signal<string | undefined> = computed(() => {
		return this.dataUrl();
	});

	public maxFileSize: number = 5 * 1024 * 1024; // 5 MB
	public maxFileSizeDisplay: string = `${(this.maxFileSize / (1024 * 1024)).toFixed(2)} MB`;

	ngOnInit(): void {
		this.dataUrl.set(this.userImage());
	}

	getDataUrl(files: FileList | null): void {
		if (!files || files.length === 0) {
			return;
		}

		const profileImageFile: File = files[0];
		if (profileImageFile) {
			const reader: FileReader = new FileReader();

			reader.onload = (e: ProgressEvent<FileReader>) => {
				if (!e.target?.result) {
					this.onErrorMessage('No file data', e);
					return;
				}

				if (profileImageFile.size > this.maxFileSize) {
					this.onErrorMessage('File is too large', e);
					this._alertService.addAlert({
						type: 'error',
						title: 'File Too Large',
						message: `File size must be less than ${this.maxFileSizeDisplay}`,
						duration: 3000
					});
					return;
				}

				this.dataUrl.set(e.target?.result as string);
				this.onFileChange.emit(e.target?.result as string);
			};

			reader.onerror = (e: ProgressEvent<FileReader>) => {
				this.onErrorMessage('Error reading file', e);
			};

			reader.readAsDataURL(profileImageFile);
		}
	}

	removeProfile(): void {
		this.dataUrl.set(undefined);
		this.onFileChange.emit(undefined);
	}

	private onErrorMessage(message: string, error: ProgressEvent<FileReader>): void {
		console.error(message, error);
	}
}
